import { useEffect, useRef, useState } from "react";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { Loader2, Sparkles } from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";

export const Route = createFileRoute("/_authenticated/create/template/$id/processing")({
  head: () => ({
    meta: [
      { title: "動画生成中 | 変身テンプレ | KAGEM" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ProcessingPage,
});

type Row = {
  status: string | null;
  template_state: string | null;
  intermediate_image_url: string | null;
  output_video_url: string | null;
  error_message: string | null;
  created_at: string | null;
};

const ETA_SECONDS = 240;

function ProcessingPage() {
  const { id } = useParams({ from: "/_authenticated/create/template/$id/processing" });
  const navigate = useNavigate();
  const [row, setRow] = useState<Row | null>(null);
  const [now, setNow] = useState(Date.now());
  const navigatedRef = useRef(false);

  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);

  useEffect(() => {
    let active = true;
    const fetchOnce = async () => {
      const { data } = await supabase
        .from("generations")
        .select(
          "status, template_state, intermediate_image_url, output_video_url, error_message, created_at",
        )
        .eq("id", id)
        .maybeSingle();
      if (!active || !data) return;
      setRow(data as Row);
    };
    void fetchOnce();
    const t = window.setInterval(fetchOnce, 3000);
    return () => {
      active = false;
      window.clearInterval(t);
    };
  }, [id]);

  // 完了 / 失敗で自動遷移
  useEffect(() => {
    if (!row || navigatedRef.current) return;
    if (row.status === "completed" || row.template_state === "completed") {
      navigatedRef.current = true;
      navigate({ to: "/create/template/$id/result", params: { id } });
    }
  }, [row, id, navigate]);

  const failed =
    row?.status === "failed" ||
    row?.template_state === "step2_failed" ||
    row?.template_state === "step1_failed";

  const startedAt = row?.created_at ? new Date(row.created_at).getTime() : now;
  const elapsed = Math.max(0, Math.floor((now - startedAt) / 1000));
  const pct = Math.min(95, Math.round((elapsed / ETA_SECONDS) * 100));
  const remaining = Math.max(0, ETA_SECONDS - elapsed);

  return (
    <main className="min-h-screen bg-background px-4 py-10">
      <div className="mx-auto max-w-2xl">
        <header className="mb-8 text-center">
          <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-3 py-1 text-xs font-semibold text-primary">
            <Sparkles className="h-3.5 w-3.5" />
            Step 2: 動画を生成中
          </div>
          <h1 className="text-2xl font-bold sm:text-3xl">変身動画を作っています</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            AIが映像を生成中です。完了したら自動で次の画面に進みます。
          </p>
        </header>

        <Card className="overflow-hidden">
          <div className="aspect-[9/16] w-full bg-muted">
            {row?.intermediate_image_url ? (
              <img
                src={row.intermediate_image_url}
                alt="合成画像"
                className="h-full w-full object-contain opacity-70"
              />
            ) : (
              <div className="flex h-full items-center justify-center">
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
              </div>
            )}
          </div>
          <div className="space-y-3 p-5">
            {failed ? (
              <div className="space-y-3 text-center">
                <p className="text-sm font-semibold text-destructive">生成に失敗しました</p>
                <p className="text-xs text-muted-foreground">
                  {row?.error_message ?? "動画の生成中にエラーが発生しました。"}
                </p>
                <p className="text-xs text-muted-foreground">
                  ※ 動画化に使った分のクレジットは自動返却されます。
                </p>
                <div className="flex justify-center gap-2 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => navigate({ to: "/create/template/$id/preview", params: { id } })}
                  >
                    プレビューに戻る
                  </Button>
                  <Button onClick={() => navigate({ to: "/create/template" })}>
                    最初からやり直す
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <Progress value={pct} />
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Loader2 className="h-3 w-3 animate-spin text-primary" />
                    生成中... ({pct}%)
                  </span>
                  <span>
                    {remaining > 0 ? `あと約 ${Math.ceil(remaining / 60)} 分` : "まもなく完了"}
                  </span>
                </div>
                <p className="pt-2 text-center text-xs text-muted-foreground">
                  この画面を閉じても処理は続行されます。完了は履歴から確認できます。
                </p>
              </>
            )}
          </div>
        </Card>
      </div>
    </main>
  );
}
